import type { Selection } from "react-aria-components";
import { useCallback, useMemo } from "react";

import { showToast } from "@/providers/ToastProvider";

import type { AdaptedQueueRow } from "../helpers/adapters";
import { isStatusSettled } from "../helpers/isStatusSettled";
import {
  useCosignatureSelectionActions,
  useCosignatureSelectionState,
} from "../../CosignatureSelectionProvider";

const MAX_SELECTION = 15;

type UseCosignatureSelectionParams = {
  rows: AdaptedQueueRow[];
  sortedRows: AdaptedQueueRow[];
};

export function useCosignatureSelection({ rows, sortedRows }: UseCosignatureSelectionParams) {
  const { selectedKeys } = useCosignatureSelectionState();
  const { selectRows } = useCosignatureSelectionActions();

  const disabledKeys = useMemo(() => {
    const settled = new Set(rows.filter((r) => isStatusSettled(r.status)).map((r) => r.id));

    if (selectedKeys !== "all" && selectedKeys.size >= MAX_SELECTION) {
      rows
        .filter((r) => !isStatusSettled(r.status) && !selectedKeys.has(r.id))
        .forEach((r) => settled.add(r.id));
    }

    return settled;
  }, [rows, selectedKeys]);

  const onSelectionChange = useCallback(
    (selection: Selection) => {
      const resolvedRows =
        selection === "all"
          ? sortedRows.filter((r) => !isStatusSettled(r.status)).slice(0, MAX_SELECTION)
          : rows.filter((r) => selection.has(r.id)).slice(0, MAX_SELECTION);

      if (resolvedRows.length === MAX_SELECTION) {
        showToast([
          {
            heading: "Maximum selection reached",
            text: "You can select up to 15 notes at a time.",
            variant: "warning",
          },
        ]);
      }

      selectRows(resolvedRows);
    },
    [rows, sortedRows, selectRows],
  );

  return { disabledKeys, onSelectionChange, selectedKeys };
}
